import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PanelTopSearch } from '@/components/panels/PanelTopSearch/PanelTopSearch';
import { SectionField } from '../../sections/SectionField/SectionField';
import { StyleField } from '@/components/panels/styles/StyleField/StyleField';
import IconAdd from '@/assets/icons/icon-add.svg?react';
import IconArrowRight from '@/assets/icons/icon-arrow-right.svg?react';
import ImageCreateNewStyles from '@/assets/images/sections/styles/create-new-styles.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  applyGlobalStyle,
  createGlobalStyle,
  deleteGlobalStyleById,
  fetchGlobalStyleList,
  updateGlobalStyleById,
} from '@/store/globalStyles/thunks';
import {
  selectGlobalStyleList,
  selectGlobalStyleListLoading,
  selectActiveGlobalStyleId,
} from '@/store/globalStyles/selectors';
import { openEditStyleModal } from '@/store/globalStyles/slice';
import type { GlobalStyle } from '@/store/globalStyles/types';
import { checkIsGradient, getColor, getTextColorStyle } from '@builder4/shared/utils/styleHelpers';
import type { Color } from '@builder4/shared/types/layout';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import { Skeleton } from '@/components/ui/skeleton';
import { selectUserAccessId } from '@/store/user/selectors';
import { parseOwnerIds } from '@/utils/sectionLibrary';
import { fetchBrandProfileList } from '@/store/brandProfiles/thunks';
import { selectBrandProfileList } from '@/store/brandProfiles/selectors';

interface StylesProps {
  className: string;
  onStylesClick?: (elementName: string) => void;
  onStylesEditClick?: (
    style: GlobalStyle,
    mode?: 'edit' | 'create',
    isBrandProfileStyle?: boolean,
  ) => void;
}

export const Styles: React.FC<StylesProps> = ({ className, onStylesClick, onStylesEditClick }) => {
  const dispatch = useAppDispatch();
  const currentPageId = useAppSelector(selectCurrentPageId);
  const allStyles = useAppSelector(selectGlobalStyleList);
  const stylesLoading = useAppSelector(selectGlobalStyleListLoading);
  const activeStyleId = useAppSelector(selectActiveGlobalStyleId);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');

  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const accessId = useAppSelector(selectUserAccessId);

  const brandProfiles = useAppSelector(selectBrandProfileList);

  const libraryOwnerIds = useMemo(
    () => parseOwnerIds(import.meta.env.VITE_SECTION_LIBRARY_OWNER_ID),
    [],
  );
  const styleLibraryEnabled = libraryOwnerIds.length > 0;
  const canPublishStyle =
    styleLibraryEnabled && typeof accessId === 'string' && libraryOwnerIds.includes(accessId);

  const cloneGlobalStyle = (style: GlobalStyle): GlobalStyle => ({
    ...style,
    globalStyle: JSON.parse(JSON.stringify(style.globalStyle)) as GlobalStyle['globalStyle'],
  });

  const myStyles = useMemo(
    () => (accessId ? allStyles.filter((style) => style.ownerId === accessId) : []),
    [accessId, allStyles],
  );
  // Published styles are available only when a library owner allowlist is configured.
  const libraryStyles = useMemo(() => {
    if (!styleLibraryEnabled) return [];
    return allStyles.filter((style) => style.published && libraryOwnerIds.includes(style.ownerId));
  }, [allStyles, libraryOwnerIds, styleLibraryEnabled]);
  // Visible styles are filtered by the current library search term.
  const filteredLibraryStyles = useMemo(() => {
    const normalizedSearch = librarySearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return libraryStyles;
    return libraryStyles.filter((style) => style.label.toLowerCase().includes(normalizedSearch));
  }, [librarySearchTerm, libraryStyles]);

  const handleClick = (elementName: string) => onStylesClick?.(elementName);

  const handleApply = (style: GlobalStyle) => {
    if (!currentPageId) return;
    dispatch(
      applyGlobalStyle({
        pageId: currentPageId,
        globalStyleId: style.id,
      }),
    );
  };

  const handleOpenLibraryStyle = (style: GlobalStyle) => {
    handleClick(ASSETS_PANEL.STYLES_EDIT);
    onStylesEditClick?.(cloneGlobalStyle(style), 'create');
  };

  // Show confirmation modal only for applied (active) global style
  // For non-applied styles, go directly to edit
  const handleEditStyleClick = (style: GlobalStyle, isBrandProfileStyle = false) => {
    if (activeStyleId === style.id) {
      // Style is applied - show confirmation modal via Redux
      dispatch(openEditStyleModal(style));
      // WHY: Must set isBrandProfileStyle in AssetsPanel state before the modal
      // renders so EditGlobalStyleModal receives the correct prop on first paint.
      onStylesEditClick?.(style, undefined, isBrandProfileStyle);
    } else {
      // Style is not applied - go directly to edit
      handleClick(ASSETS_PANEL.STYLES_EDIT);
      onStylesEditClick?.(style, undefined, isBrandProfileStyle);
    }
  };

  const handleRename = (style: GlobalStyle) => {
    setRenamingId(style.id);
    setRenameValue(style.label);
  };

  const commitRename = (style: GlobalStyle) => {
    if (!renamingId) return;
    const next = renameValue.trim();
    if (next)
      dispatch(
        updateGlobalStyleById({
          styleId: renamingId,
          body: { label: next, global_style: style.globalStyle },
          showSuccessToast: next !== style.label,
        }),
      );
    setRenamingId(null);
  };

  const cancelRename = () => setRenamingId(null);

  const handleDuplicate = (style: GlobalStyle) => {
    const payload = {
      label: `${style?.label} Copy`,
      global_style: style?.globalStyle,
      isDuplicate: true,
    };
    dispatch(createGlobalStyle(payload));
  };

  const handleRemove = (id: string) => dispatch(deleteGlobalStyleById(id));

  useEffect(() => {
    dispatch(fetchGlobalStyleList());
    dispatch(fetchBrandProfileList());
  }, [dispatch]);

  const handlePublish = (style: GlobalStyle) => {
    dispatch(
      updateGlobalStyleById({
        styleId: style.id,
        body: {
          label: style.label,
          global_style: style.globalStyle,
          published: true,
        },
      }),
    );
  };

  const handleUnpublish = (style: GlobalStyle) => {
    dispatch(
      updateGlobalStyleById({
        styleId: style.id,
        body: {
          label: style.label,
          global_style: style.globalStyle,
          published: false,
        },
      }),
    );
  };

  const getBlockTextStyle = (style: GlobalStyle): React.CSSProperties => ({
    fontFamily: style.globalStyle.typography.heading.fontFamily,
    fontSize: 16,
    fontWeight: style.globalStyle.typography.heading.h1.fontWeight,
    letterSpacing: `${style.globalStyle.typography.heading.h1.letterSpacing}px`,
    ...(typeof style.globalStyle.typography.heading.h1.fontColor === 'string'
      ? { color: style.globalStyle.typography.heading.h1.fontColor }
      : getTextColorStyle(style.globalStyle.typography.heading.h1.fontColor as Color)),
  });

  return (
    <div className={clsx(className, 'flex h-full max-w-[280px] flex-col')}>
      <PanelTopHeader
        text="Styles"
        backButton={false}
        onClickClose={() => handleClick('')}
        className={''}
      />

      <ScrollArea className="flex w-full flex-1 gap-1 overflow-hidden px-6">
        <div className="flex flex-col gap-4 pt-6 pb-5">
          <div className="flex flex-col gap-1">
            <SectionField
              className=""
              icon={IconAdd}
              imageSrc={ImageCreateNewStyles}
              text="Create New Style"
              data-qa-selector="styles-create-new-button"
              onClick={() => handleClick('Styles New')}
            />
          </div>
        </div>
        {brandProfiles.length > 0 && (
          <>
            <hr className="my-1 w-full border-neutral-100 bg-neutral-100" />
            <div className="flex flex-col gap-3 py-4">
              <div className="flex items-center justify-between pb-1">
                <p className="font-['Inter'] text-xs font-semibold text-neutral-900">Brand Kit</p>
                <a
                  href={`${import.meta.env.VITE_API_MAIN_APP}/#/brand`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-['Inter'] text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Manage ↗
                </a>
              </div>
              {brandProfiles.map((brandProfile) => {
                const linkedStyle = brandProfile.globalStyleId
                  ? (allStyles.find((s) => s.id === brandProfile.globalStyleId) ?? null)
                  : null;
                const isActive = linkedStyle ? activeStyleId === linkedStyle.id : false;
                return (
                  <StyleField
                    key={brandProfile.id}
                    className="w-full"
                    text={brandProfile.name}
                    variant="withActions"
                    blockText="Aa"
                    blockVariant={
                      linkedStyle && checkIsGradient(linkedStyle.globalStyle.colors.color1.value)
                        ? 'gradient'
                        : 'solid'
                    }
                    blockTextStyle={linkedStyle ? getBlockTextStyle(linkedStyle) : undefined}
                    blockBgColor={
                      linkedStyle && !checkIsGradient(linkedStyle.globalStyle.colors.color1.value)
                        ? getColor(linkedStyle.globalStyle.colors.color1.value)
                        : undefined
                    }
                    blockGradientStyle={
                      linkedStyle && checkIsGradient(linkedStyle.globalStyle.colors.color1.value)
                        ? { backgroundImage: getColor(linkedStyle.globalStyle.colors.color1.value) }
                        : undefined
                    }
                    isActive={isActive}
                    onApply={linkedStyle && !isActive ? () => handleApply(linkedStyle) : undefined}
                    isBrandProfileStyle
                    onEditStyle={
                      linkedStyle
                        ? () =>
                            // Substitute brand profile name as the display label; the underlying
                            // style ID is unchanged so Redux update operations are unaffected.
                            handleEditStyleClick({ ...linkedStyle, label: brandProfile.name }, true)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </>
        )}
        <hr className="my-1 w-full border-neutral-100 bg-neutral-100" />
        <div className="flex flex-col gap-4 py-4">
          <p className="justify-start pb-1 font-['Inter'] text-xs font-semibold text-neutral-900">
            My Styles
          </p>
          {stylesLoading ? (
            <div className="flex w-full flex-col gap-2">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          ) : myStyles.length > 0 ? (
            <div className="flex w-full flex-col gap-1">
              {myStyles.map((style) => {
                return (
                  <StyleField
                    key={style.id}
                    className="w-full"
                    text={style.label}
                    variant="withActions"
                    blockText="Aa"
                    blockVariant={
                      checkIsGradient(style.globalStyle.colors.color1.value) ? 'gradient' : 'solid'
                    }
                    blockTextStyle={getBlockTextStyle(style)}
                    blockBgColor={
                      checkIsGradient(style.globalStyle.colors.color1.value)
                        ? undefined
                        : getColor(style.globalStyle.colors.color1.value)
                    }
                    blockGradientStyle={
                      checkIsGradient(style.globalStyle.colors.color1.value)
                        ? { backgroundImage: getColor(style.globalStyle.colors.color1.value) }
                        : undefined
                    }
                    isActive={activeStyleId === style.id}
                    showPublishedChip={style.published}
                    data-qa-selector={`styles-my-style-${style.id}-item`}
                    onApply={() => handleApply(style)}
                    onEditStyle={() => handleEditStyleClick(style)}
                    onRename={() => handleRename(style)}
                    onDuplicate={() => handleDuplicate(style)}
                    onRemoveStyle={() => handleRemove(style.id)}
                    onPublish={
                      canPublishStyle && !style.published ? () => handlePublish(style) : undefined
                    }
                    onUnpublish={
                      canPublishStyle && style.published ? () => handleUnpublish(style) : undefined
                    }
                    isRenaming={renamingId === style.id}
                    renameValue={renameValue}
                    onRenameChange={setRenameValue}
                    onRenameCommit={() => commitRename(style)}
                    onRenameCancel={cancelRename}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-center text-neutral-500">No styles created yet.</p>
          )}
          {styleLibraryEnabled ? (
            <>
              <hr className="my-1 w-full border-neutral-100 bg-neutral-100" />
              <div className="flex flex-col gap-6">
                <p className="justify-start font-['Inter'] text-xs font-semibold text-neutral-900">
                  Styles Library
                </p>
                <PanelTopSearch
                  className="w-full"
                  padded={false}
                  containerClassName="h-9"
                  inputClassName="h-9 p-2 text-xs font-medium"
                  placeholder="Search styles..."
                  text=""
                  onChange={(event) => setLibrarySearchTerm(event.target.value)}
                  onSearch={(value: string) => setLibrarySearchTerm(value)}
                />
                {stylesLoading ? (
                  <div className="flex w-full flex-col gap-2">
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                ) : filteredLibraryStyles.length > 0 ? (
                  <div className="flex w-full flex-col gap-1">
                    {filteredLibraryStyles.map((style) => {
                      return (
                        <StyleField
                          key={style.id}
                          className="w-full"
                          text={style.label}
                          variant="simple"
                          icon={IconArrowRight}
                          showBlock
                          blockText="Aa"
                          blockVariant={
                            checkIsGradient(style.globalStyle.colors.color1.value)
                              ? 'gradient'
                              : 'solid'
                          }
                          blockTextStyle={getBlockTextStyle(style)}
                          blockBgColor={
                            checkIsGradient(style.globalStyle.colors.color1.value)
                              ? undefined
                              : getColor(style.globalStyle.colors.color1.value)
                          }
                          blockGradientStyle={
                            checkIsGradient(style.globalStyle.colors.color1.value)
                              ? { backgroundImage: getColor(style.globalStyle.colors.color1.value) }
                              : undefined
                          }
                          data-qa-selector={`styles-library-${style.id}-item`}
                          onClick={() => handleOpenLibraryStyle(style)}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-neutral-500">
                    {librarySearchTerm ? 'No styles found.' : 'No styles yet.'}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
};
